import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { EmailContentType, Emptyable } from "@simplyk/common";

import { useRecipientPlaceholder } from "../../EmailRecipientEditor/useRecipientPlaceholder";

import { useTranslate } from "@/common/hooks/useTranslate";
import { EmailEditorDevice, useEmailEditorDevice } from "@/components/EmailEditorDevice/EmailEditorDeviceContext";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Expand, Cross as Close } from "@/icons/outlined";

interface EmailEditorHeaderProps {
  handleClose: () => void;
  recipientsCount: number;
  title?: Emptyable<string>;
  previewOpened?: boolean;
  openPreview?: () => void;
  closePreview?: () => void;
  emailType: Emptyable<EmailContentType>;
  hidePreviewButton?: boolean;
}

export const EmailEditorHeader = ({
  handleClose,
  recipientsCount,
  title,
  previewOpened,
  openPreview,
  closePreview,
  emailType,
  hidePreviewButton = false,
}: EmailEditorHeaderProps) => {
  const device = useEmailEditorDevice();
  const isMobile = device === EmailEditorDevice.Mobile;
  const { t } = useTranslate();

  const recipientPlaceholder = useRecipientPlaceholder({ emailType, hasRecipients: Boolean(recipientsCount) });

  const closeButton = (
    <DashboardIconButton
      onClick={handleClose}
      size={isMobile ? "medium" : "small"}
      vibe="neutral"
      data-test="compose-email-write-email-drawer-close-button"
    >
      <Close />
    </DashboardIconButton>
  );

  const heading =
    title ??
    // If there is a recipient placeholder, it means the recipients will be dynamically added to the email
    // We don't have to show the count
    (recipientPlaceholder
      ? t("dashboard", "emailEditor.createYourEmail")
      : t("dashboard", recipientsCount > 1 ? "tickets.sendToParticipants" : "tickets.sendToParticipant", {
          count: recipientsCount,
        }));

  if (isMobile) {
    return (
      <Stack direction="row" alignItems="center" gap={1.5}>
        {closeButton}
        <DashboardTypography variant="h5" sx={(theme) => ({ flex: 1, ...theme.mixins.limitTextToNLines(2) })}>
          {heading}
        </DashboardTypography>
      </Stack>
    );
  }

  return (
    <Grid container sx={{ position: "relative", alignItems: "center", wrap: "nowrap" }}>
      <Grid item marginRight={1}>
        {closeButton}
      </Grid>
      <Grid item xs sx={{ overflow: "hidden" }}>
        <DashboardTypography variant="h5" sx={(theme) => ({ textAlign: "center", ...theme.mixins.truncateOverflow })}>
          {heading}
        </DashboardTypography>
      </Grid>
      {!hidePreviewButton && (
        <Grid item marginRight={1}>
          <DashboardButton
            onClick={previewOpened ? closePreview : openPreview}
            size="medium"
            vibe="brand"
            variant="outlined"
            data-test="compose-email-write-email-drawer-close-button"
            endIcon={previewOpened ? <Close /> : <Expand />}
          >
            {t("dashboard", "preview")}
          </DashboardButton>
        </Grid>
      )}
    </Grid>
  );
};
